import { useState } from 'react';
import type { FormEvent } from 'react';
import SectionHeader from './SectionHeader';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dagnq79tto0ceqqni190';

const notice = [
  { icon: 'ri-upload-cloud-2-line', title: '投稿表情包', text: '带上事件背景和原图，审核通过即可上榜二创展示。' },
  { icon: 'ri-search-eye-line', title: '找一张老梗图', text: '描述人物、动作或台词，仓库君帮你在全网捞回来。' },
  { icon: 'ri-flag-line', title: '投诉 / 建议', text: '对内容或二创有异议，来这里告诉我们，当天处理。' },
];

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'ok' | 'err'>('idle');
  const [msg, setMsg] = useState('');

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get('company_alt') || '').trim();
    if (honeypot) {
      setStatus('ok');
      setMsg('收到啦，仓库君已把你的投稿提上日程！');
      form.reset();
      return;
    }
    fd.delete('company_alt');
    try {
      const res = await fetch(SUBMIT_ADDR, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: new URLSearchParams(fd as unknown as Record<string, string>) });
      const responseText = await res.text();
      let parsed: Record<string, any> | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || '';
      const ok = res.ok && parsed?.code === 'OK' && !/spam/i.test(responseText + serverMsg);
      if (ok) {
        setStatus('ok');
        setMsg('发送成功！仓库君这就安排，冲上热榜别忘回来领功～');
        form.reset();
      } else {
        setStatus('err');
        setMsg(/spam/i.test(responseText + serverMsg) ? '系统检测到疑似垃圾请求，请稍后再试。' : serverMsg || '提交失败了，请检查网络或稍后重试。');
      }
    } catch {
      setStatus('err');
      setMsg('网络似乎开小差了，请稍后再试一次。');
    }
  };

  return (
    <section id="contact" className="scroll-mt-20 bg-background-50 py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeader
          kicker="📮 投稿 & 求图"
          title="有话跟仓库君说？"
          desc="想投稿自己的梗图、求一张全网在用的老图，或是有任何建议，填下表就能一键送达。"
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.85fr_1.15fr]">
          {/* 说明区 */}
          <div className="flex flex-col gap-4">
            {notice.map((n) => (
              <div key={n.title} className="flex gap-4 rounded-3xl bg-background-100 p-5 ring-1 ring-background-200">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-accent-100 text-accent-700"><i className={`${n.icon} text-2xl`}></i></span>
                <div>
                  <h3 className="font-bold text-foreground-900">{n.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">{n.text}</p>
                </div>
              </div>
            ))}
            <div className="rounded-3xl bg-gradient-to-br from-secondary-500 to-secondary-600 p-6 text-background-50">
              <h3 className="font-heading text-xl">投稿被选中的好处</h3>
              <ul className="mt-3 space-y-2 text-sm text-background-50/90">
                <li className="flex items-center gap-2"><i className="ri-checkbox-circle-fill"></i>网站显著位置署上你的昵称</li>
                <li className="flex items-center gap-2"><i className="ri-checkbox-circle-fill"></i>解锁“二创达人”专属徽章</li>
                <li className="flex items-center gap-2"><i className="ri-checkbox-circle-fill"></i>你的图可能被全网疯传</li>
              </ul>
            </div>
          </div>

          {/* 表单区 */}
          <form onSubmit={onSubmit} data-readdy-form="contact" className="rounded-3xl bg-background-100 p-6 ring-1 ring-background-200 md:p-8">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-sm font-bold text-foreground-800">你的昵称</span>
                <input name="name" type="text" placeholder="如何称呼你" required className="w-full rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200" />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-bold text-foreground-800">联系邮箱</span>
                <input name="email" type="email" placeholder="name@example.com" required className="w-full rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200" />
              </label>
            </div>

            <label className="mt-4 block">
              <span className="mb-1.5 block text-sm font-bold text-foreground-800">我想…</span>
              <select name="topic" className="w-full rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200">
                <option value="投稿表情包">投稿表情包</option>
                <option value="求一张老梗图">求一张老梗图</option>
                <option value="内容投诉">内容投诉</option>
                <option value="合作 / 建议">合作 / 建议</option>
              </select>
            </label>

            <label className="mt-4 block">
              <span className="mb-1.5 block text-sm font-bold text-foreground-800">详细内容</span>
              <textarea
                name="message"
                rows={5}
                maxLength={500}
                required
                placeholder="请描述事件 / 图的特征、投稿要求或你想说的话（500字以内）"
                className="w-full resize-none rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              ></textarea>
              <span className="mt-1 block text-right text-xs text-foreground-400">最多 500 字</span>
            </label>

            <input className="field-honeypot" type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

            <button
              type="submit"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3.5 text-base font-bold text-background-50 transition-colors hover:bg-primary-600"
            >
              发送给仓库君 <i className="ri-send-plane-fill"></i>
            </button>

            {status !== 'idle' && (
              <p className={`mt-4 flex items-start gap-2 rounded-xl p-3.5 text-sm ${
                status === 'ok' ? 'bg-accent-100 text-accent-900' : 'bg-primary-100 text-primary-800'
              }`}>
                <i className={`mt-0.5 ${status === 'ok' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'}`}></i>
                <span role="status">{msg}</span>
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}